import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/games";

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  `whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition ${
    isActive ? "bg-primary-100 text-primary-700" : "text-foreground-700 hover:bg-background-100 hover:text-primary-600"
  }`;

export default function Navbar() {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [mobileKeyword, setMobileKeyword] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const submitSearch = (value: string) => {
    const trimmed = value.trim();
    navigate(trimmed ? `/search?q=${encodeURIComponent(trimmed)}` : "/search");
    setMobileOpen(false);
    setMobileSearchOpen(false);
  };

  return (
    <header className="sticky top-0 z-40 border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="mx-auto flex h-16 max-w-screen2xl items-center justify-between gap-4 px-4 md:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2" aria-label="网页游戏直接玩 首页">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
            <i className="ri-gamepad-line text-xl"></i>
          </span>
          <span className="text-lg font-black tracking-tight text-foreground-900">
            网页游戏<span className="text-primary-500">直接玩</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex" aria-label="主导航">
          <NavLink to="/" className={navLinkClass} end>
            首页
          </NavLink>
          <div className="group relative" ref={dropdownRef}>
            <button
              type="button"
              className="flex items-center gap-1 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium text-foreground-700 transition hover:bg-background-100 hover:text-primary-600"
            >
              游戏分类
              <i className="ri-arrow-down-s-line transition group-hover:rotate-180"></i>
            </button>
            <div className="invisible absolute left-0 top-full z-40 w-[420px] pt-2 opacity-0 transition group-hover:visible group-hover:opacity-100">
              <div className="grid grid-cols-2 gap-1 rounded-lg border border-background-200 bg-background-50 p-2">
                {categories.map((category) => (
                  <Link
                    key={category.key}
                    to={`/category/${category.key}`}
                    className="flex items-center gap-3 rounded-md px-3 py-2 transition hover:bg-background-100"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                      <i className={`${category.icon} text-lg`}></i>
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-bold text-foreground-900">{category.name}</span>
                      <span className="block truncate text-xs text-foreground-500">{category.desc}</span>
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
          <NavLink to="/about" className={navLinkClass}>
            关于我们
          </NavLink>
        </nav>

        <form
          className="hidden items-center gap-2 md:flex"
          onSubmit={(event) => {
            event.preventDefault();
            submitSearch(keyword);
          }}
          role="search"
        >
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
            <input
              type="text"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索游戏名称或类型"
              className="w-56 rounded-md border border-background-300 bg-background-50 py-2 pl-9 pr-3 text-sm text-foreground-900 outline-none transition placeholder:text-foreground-400 focus:border-primary-400"
            />
          </div>
          <button
            type="submit"
            className="inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-bold text-background-50 transition hover:bg-primary-600"
          >
            搜索
          </button>
        </form>

        <div className="flex items-center gap-1 md:hidden">
          <button
            type="button"
            aria-label="打开搜索"
            onClick={() => setMobileSearchOpen((prev) => !prev)}
            className="flex h-10 w-10 items-center justify-center rounded-md text-foreground-700 transition hover:bg-background-100"
          >
            <i className="ri-search-line text-xl"></i>
          </button>
          <button
            type="button"
            aria-label="打开菜单"
            onClick={() => setMobileOpen((prev) => !prev)}
            className="flex h-10 w-10 items-center justify-center rounded-md text-foreground-700 transition hover:bg-background-100"
          >
            <i className={mobileOpen ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"}></i>
          </button>
        </div>
      </div>

      {mobileSearchOpen && (
        <div className="border-t border-background-200 px-4 py-3 md:hidden">
          <form
            className="flex items-center gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              submitSearch(mobileKeyword);
            }}
            role="search"
          >
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
              <input
                type="text"
                value={mobileKeyword}
                onChange={(event) => setMobileKeyword(event.target.value)}
                placeholder="搜索游戏名称或类型"
                className="w-full rounded-md border border-background-300 bg-background-50 py-2 pl-9 pr-3 text-sm text-foreground-900 outline-none transition placeholder:text-foreground-400 focus:border-primary-400"
              />
            </div>
            <button
              type="submit"
              className="inline-flex items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-bold text-background-50"
            >
              搜索
            </button>
          </form>
        </div>
      )}

      {mobileOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-6 pt-3 md:hidden">
          <div className="flex flex-col gap-1">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="rounded-md px-3 py-2.5 text-sm font-medium text-foreground-800 transition hover:bg-background-100"
            >
              首页
            </Link>
            <Link
              to="/about"
              onClick={() => setMobileOpen(false)}
              className="rounded-md px-3 py-2.5 text-sm font-medium text-foreground-800 transition hover:bg-background-100"
            >
              关于我们
            </Link>
          </div>
          <p className="mt-4 px-3 text-xs font-bold uppercase tracking-wide text-foreground-400">游戏分类</p>
          <div className="mt-2 grid grid-cols-2 gap-2">
            {categories.map((category) => (
              <Link
                key={category.key}
                to={`/category/${category.key}`}
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2 rounded-md border border-background-200 px-3 py-2.5 transition hover:border-primary-300"
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                  <i className={`${category.icon} text-base`}></i>
                </span>
                <span className="text-sm font-medium text-foreground-800">{category.name}</span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}